import React, { useState, useEffect, useRef, useCallback } from 'react';
import { XMarkIcon, TrashIcon, PauseIcon, PlayIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { MediaRecorder } from 'extendable-media-recorder';
import { userService } from '../services/UserService';
import { MAX_RECORDING_DURATION } from '../constants';

/**
 * Escribe una cadena de texto en el DataView.
 * @param {DataView} view 
 * @param {number} offset 
 * @param {string} string 
 */
const writeString = (view, offset, string) => {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
};

/**
 * Convierte un AudioBuffer a un Blob en formato WAV.
 * @param {AudioBuffer} buffer 
 * @returns {Blob}
 */
const audioBufferToWav = (buffer) => {
  const numOfChan = buffer.numberOfChannels;
  const length = buffer.length * numOfChan * 2 + 44;
  const bufferArray = new ArrayBuffer(length);
  const view = new DataView(bufferArray);

  /* RIFF identifier */
  writeString(view, 0, 'RIFF');
  /* file length */
  view.setUint32(4, 36 + buffer.length * numOfChan * 2, true);
  /* RIFF type */
  writeString(view, 8, 'WAVE');
  /* format chunk identifier */
  writeString(view, 12, 'fmt ');
  /* format chunk length */
  view.setUint32(16, 16, true);
  /* sample format (raw) */
  view.setUint16(20, 1, true);
  /* channel count */
  view.setUint16(22, numOfChan, true);
  /* sample rate */
  view.setUint32(24, 16000, true);
  /* byte rate (sample rate * block align) */
  view.setUint32(28, 16000 * numOfChan * 2, true);
  /* block align (channel count * bytes per sample) */
  view.setUint16(32, numOfChan * 2, true);
  /* bits per sample */
  view.setUint16(34, 16, true);
  /* data chunk identifier */
  writeString(view, 36, 'data');
  /* data chunk length */
  view.setUint32(40, buffer.length * numOfChan * 2, true);

  // Write interleaved data
  let offset = 44;
  for (let i = 0; i < buffer.length; i++) {
    for (let channel = 0; channel < numOfChan; channel++) {
      let sample = buffer.getChannelData(channel)[i];
      // Clipping
      sample = Math.max(-1, Math.min(1, sample));
      // Convert to 16-bit PCM
      sample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
      view.setInt16(offset, sample, true);
      offset += 2;
    }
  }

  return new Blob([view], { type: 'audio/wav' });
};

/**
 * Procesa el blob de audio para reducir la frecuencia de muestreo y convertir a mono.
 * @param {Blob} blob 
 * @returns {Promise<Blob>}
 */
const processAudioBlob = async (blob) => {
  const arrayBuffer = await blob.arrayBuffer();
  const audioContext = new (window.AudioContext || window.webkitAudioContext)();
  const decodedData = await audioContext.decodeAudioData(arrayBuffer);

  // Crear OfflineAudioContext para re-muestrear
  const offlineContext = new OfflineAudioContext(1, decodedData.duration * 16000, 16000);

  // Crear buffer fuente
  const source = offlineContext.createBufferSource();
  source.buffer = decodedData;

  // Conectar y renderizar
  source.connect(offlineContext.destination);
  source.start(0);
  const renderedBuffer = await offlineContext.startRendering();

  // Convertir a Blob WAV
  const wavBlob = audioBufferToWav(renderedBuffer);
  return wavBlob;
};

function RecordingPanel({ isOpen, onClose, onSend }) {
  const { t } = useTranslation();
  const [isPaused, setIsPaused] = useState(false);
  const [duration, setDuration] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null); // null = primary email
  const [userData, setUserData] = useState(null);

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerIntervalRef = useRef(null);
  const durationRef = useRef(0);
  const isCancelledRef = useRef(false);
  const wakeLockRef = useRef(null);

  // Subscribe to userService for user data
  useEffect(() => {
    const updateUserData = (user) => {
      setUserData(user);
    };

    // Initial load
    updateUserData(userService.currentUser);

    // Subscribe to changes
    const unsubscribe = userService.subscribe(updateUserData);

    return unsubscribe;
  }, []);

  // Función auxiliar para obtener la duración máxima
  const getMaxDuration = useCallback(() => {
    return userService.currentUser?.maxNoteLengthSec || MAX_RECORDING_DURATION;
  }, []);

  const stopTimer = useCallback(() => {
    console.log('stopTimer called');
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    console.log('startTimer called');
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    timerIntervalRef.current = setInterval(() => {
      setDuration(prev => {
        const newDuration = prev + 1;
        console.log('Timer tick, new duration:', newDuration);
        durationRef.current = newDuration;

        // Usar getMaxDuration() en lugar de MAX_RECORDING_DURATION
        if (newDuration >= getMaxDuration()) {
          console.log('Duración máxima alcanzada. Deteniendo la grabación.');
          if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.pause();
            setIsPaused(true);
            stopTimer();
            alert(t('recording.maxDurationReached'));
          }
        }

        return newDuration;
      });
    }, 1000);
  }, [getMaxDuration, stopTimer, t]);

  const handlePause = useCallback(() => {
    console.log('handlePause called');
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      stopTimer();
    }
  }, [stopTimer]);

  const handleResume = useCallback(() => {
    console.log('handleResume called');
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      startTimer();
    }
  }, [startTimer]);

  const releaseWakeLock = useCallback(async () => {
    if (wakeLockRef.current) {
      try {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
        console.log('Wake Lock liberado');
      } catch (err) {
        console.error('Error al liberar Wake Lock:', err);
        wakeLockRef.current = null;
      }
    }
  }, []);

  const handleSend = useCallback(() => {
    console.log('handleSend called, current duration:', durationRef.current);
    releaseWakeLock(); // Liberar wake lock al enviar
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  }, [releaseWakeLock]);

  // Get all available emails
  const getAllEmails = useCallback(() => {
    const emails = [];
    const primaryEmail = userData?.sendEmailAddress || userData?.email;
    if (primaryEmail) {
      emails.push({ value: null, label: primaryEmail, isPrimary: true });
    }
    if (userData?.secondarySendEmails && userData.secondarySendEmails.length > 0) {
      userData.secondarySendEmails.forEach(email => {
        emails.push({ value: email, label: email, isPrimary: false });
      });
    }
    return emails;
  }, [userData]);

  const hasMultipleEmails = getAllEmails().length > 1;

  const handleCancel = useCallback(() => {
    console.log('handleCancel called');
    isCancelledRef.current = true;
    
    if (mediaRecorderRef.current) {
      if (mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      mediaRecorderRef.current = null;
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    chunksRef.current = [];
    stopTimer();
    setIsRecording(false);
    setIsPaused(false);
    setDuration(0);
    
    // Mover la liberación del wake lock al final
    releaseWakeLock();
    
    onClose(true);
  }, [onClose, stopTimer, releaseWakeLock]);

  const calculateProgress = (currentDuration) => {
    // Usar getMaxDuration() en lugar de MAX_RECORDING_DURATION
    return Math.min((currentDuration / getMaxDuration()) * 100, 100);
  };

  const requestWakeLock = useCallback(async () => {
    try {
      if ('wakeLock' in navigator && !wakeLockRef.current) {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        console.log('Wake Lock activado');
        // Quitamos el event listener para evitar reactivaciones automáticas
      }
    } catch (err) {
      console.error('Error al solicitar Wake Lock:', err);
    }
  }, []);

  useEffect(() => {
    console.log('useEffect for recorder initialization');
    const initRecorder = async () => {
      try {
        await requestWakeLock();

        const stream = await navigator.mediaDevices.getUserMedia({ 
          audio: {
            echoCancellation: true,
          }
        });
        streamRef.current = stream;

        const recorder = new MediaRecorder(stream, {
          mimeType: 'audio/wav'
        });
        mediaRecorderRef.current = recorder;

        recorder.ondataavailable = (event) => {
          if (!isCancelledRef.current) {
            chunksRef.current.push(event.data);
          }
        };

        recorder.onstart = () => {
          console.log('Recorder started');
          setIsRecording(true);
          setDuration(0);
          durationRef.current = 0;

           if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
           timerIntervalRef.current = setInterval(() => {
            setDuration(prev => {
              const newDuration = prev + 1;
              durationRef.current = newDuration;
              const maxDur = userService.currentUser?.maxNoteLengthSec || MAX_RECORDING_DURATION;
              
              if (newDuration >= maxDur) {
                if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                    mediaRecorderRef.current.pause();
                    setIsPaused(true);
                    if (timerIntervalRef.current) {
                        clearInterval(timerIntervalRef.current);
                        timerIntervalRef.current = null;
                    }
                    alert(t('recording.maxDurationReached'));
                }
              }
              return newDuration;
            });
           }, 1000);
        };

        recorder.onpause = () => {
           if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
           }
        };

        recorder.onresume = () => {
           if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
           timerIntervalRef.current = setInterval(() => {
            setDuration(prev => {
              const newDuration = prev + 1;
              durationRef.current = newDuration;
              const maxDur = userService.currentUser?.maxNoteLengthSec || MAX_RECORDING_DURATION;
              
              if (newDuration >= maxDur) {
                if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                    mediaRecorderRef.current.pause();
                    setIsPaused(true);
                    if (timerIntervalRef.current) {
                        clearInterval(timerIntervalRef.current);
                        timerIntervalRef.current = null;
                    }
                    alert(t('recording.maxDurationReached'));
                }
              }
              return newDuration;
            });
           }, 1000);
        };

        recorder.onstop = async () => {
          console.log('Recorder stopped, final duration:', durationRef.current);
          if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
          }
          setIsRecording(false);
          // releaseWakeLock(); // call ref version or local?
          // We can't easily call the React prop 'releaseWakeLock' if it's a dependency.
          // But we have access to wakeLockRef directly.
          if (wakeLockRef.current) {
             try { await wakeLockRef.current.release(); wakeLockRef.current = null; } catch(e) {}
          }
          
          if (!isCancelledRef.current) {
            try {
              let audioBlob = new Blob(chunksRef.current, { type: 'audio/wav' });
              console.log('Audio blob created, size:', audioBlob.size);

              // Procesar el blob para reducir la frecuencia y convertir a mono
              audioBlob = await processAudioBlob(audioBlob);
              console.log('Processed audio blob size:', audioBlob.size);

              if (durationRef.current >= 5) {
                console.log('Sending audio, duration:', durationRef.current, 'selectedEmail:', selectedEmail);
                onSend(audioBlob, durationRef.current, selectedEmail);
              } else {
                console.log('Audio too short, duration:', durationRef.current);
                alert(t('recording.minimumDurationRequired'));
              }
              chunksRef.current = [];
              onClose(false);
            } catch (error) {
              console.error('Error processing audio:', error);
              alert(t('recording.errorProcessingAudio') + ' ' + error.message);
              onClose(true);
            }
          }
        };

        recorder.start();
        console.log('Recorder initialized and started');
      } catch (error) {
        console.error('Error initializing recorder:', error);
        if (wakeLockRef.current) {
            try { await wakeLockRef.current.release(); wakeLockRef.current = null; } catch(e) {}
        }
        alert(t('recording.failedToInitializeRecorder') + ' ' + error.message);
        onClose(true);
      }
    };

    initRecorder();

    return () => {
      if (isCancelledRef.current) {
        return;
      }
      
      console.log('Cleaning up recording...');
      isCancelledRef.current = true;
      
      if (mediaRecorderRef.current) {
        if (mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop();
        }
        mediaRecorderRef.current = null;
      }
      
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      
      chunksRef.current = [];
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      setIsRecording(false);
      setIsPaused(false);
      setDuration(0);
      if (wakeLockRef.current) {
         // We need to call the async function but can't await in cleanup easily.
         // Just calling release() is fine.
         wakeLockRef.current.release().catch(e => console.error(e));
         wakeLockRef.current = null;
      }
      onClose(true);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, onSend, requestWakeLock]); // selectedEmail is used in onstop callback but shouldn't trigger re-initialization

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
    const secs = (seconds % 60).toString().padStart(2, '0');
    const maxDuration = getMaxDuration();
    return `${mins}:${secs}/${Math.floor(maxDuration / 60).toString().padStart(2, '0')}:00`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white rounded-lg shadow-lg p-6 w-80">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">{t('recording.recordingNote')}</h2>
          <button onClick={handleCancel} className="text-gray-500 hover:text-gray-700">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>
        <div className="text-center mb-4">
          {/* Email selector - only show if there are multiple emails */}
          {hasMultipleEmails && (
            <div className="mb-3">
              <select
                value={selectedEmail || ''}
                onChange={(e) => setSelectedEmail(e.target.value || null)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent cursor-pointer max-w-[90%] truncate"
              >
                {getAllEmails().map((email) => (
                  <option key={email.value || 'primary'} value={email.value || ''}>
                    {email.label} {email.isPrimary ? t('recording.primary') : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          <p className="text-gray-700 mb-2">{formatTime(duration)}</p>
          <div className="w-full bg-gray-200 rounded-full h-1.5 mb-4 my-4">
            <div 
              className="bg-orange-500 h-1.5 rounded-full transition-all duration-300 ease-in-out" 
              style={{ width: `${calculateProgress(duration)}%` }}
            ></div>
          </div>
        </div>
        <div className="flex justify-around">
          <button
            onClick={handleCancel}
            className="p-2 bg-gray-200 hover:bg-gray-300 rounded-full"
            title={t('recording.cancelRecording')}
          >
            <TrashIcon className="w-6 h-6 text-red-500" />
          </button>
          {isPaused ? (
            <button
              onClick={handleResume}
              className="p-2 bg-gray-200 hover:bg-gray-300 rounded-full"
              title={t('recording.resumeRecording')}
            >
              <PlayIcon className="w-6 h-6 text-blue-500" />
            </button>
          ) : (
            <button
              onClick={handlePause}
              className="p-2 bg-gray-200 hover:bg-gray-300 rounded-full"
              title={t('recording.pauseRecording')}
            >
              <PauseIcon className="w-6 h-6 text-yellow-500" />
            </button>
          )}
          <button
            onClick={handleSend}
            disabled={duration < 5 || !isRecording}
            className={`p-2 bg-gray-200 hover:bg-gray-300 rounded-full ${(duration < 5 || !isRecording) ? 'opacity-50 cursor-not-allowed' : ''}`}
            title={t('recording.sendRecording')}
          >
            <PaperAirplaneIcon className="w-6 h-6 text-green-500 transform rotate-45" />
          </button>
        </div>
      </div>
    </div>
  );
}

RecordingPanel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSend: PropTypes.func.isRequired,
};

export default RecordingPanel;